/**
 * Brand Promise Bar (E20-1065)
 *
 * On mobile the cards scroll horizontally (native CSS scroll-snap). This custom
 * element wires up the pagination dots: the active dot reflects whichever card
 * is snapped into view (derived from scroll position), and clicking a dot scrolls
 * the row to that card.
 */
class BrandPromiseBar extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.brand-promise-bar__items');
    this.items = Array.from(this.querySelectorAll('.brand-promise-bar__item'));
    this.dots = Array.from(this.querySelectorAll('.brand-promise-bar__dot'));
    if (!this.scroller || this.dots.length === 0) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.ticking = false;

    this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToItem(index), { signal });
    });

    this.updateActive();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateActive();
      this.ticking = false;
    });
  }

  // The card snapped into view is the one whose start offset is nearest the
  // current scroll position — this matches where CSS scroll-snap lands.
  updateActive() {
    const { scrollLeft } = this.scroller;
    let activeIndex = 0;
    let min = Infinity;
    this.items.forEach((item, i) => {
      const dist = Math.abs(this.itemStart(item) - scrollLeft);
      if (dist < min) {
        min = dist;
        activeIndex = i;
      }
    });
    this.setActive(activeIndex);
  }

  // Scroll offset at which a card snaps to the row's start edge (scroll-independent).
  itemStart(item) {
    return (
      item.getBoundingClientRect().left -
      this.scroller.getBoundingClientRect().left +
      this.scroller.scrollLeft
    );
  }

  scrollToItem(index) {
    const item = this.items[index];
    if (!item) return;
    this.scroller.scrollTo({ left: this.itemStart(item), behavior: 'smooth' });
  }

  setActive(index) {
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('brand-promise-bar__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('brand-promise-bar')) {
  customElements.define('brand-promise-bar', BrandPromiseBar);
}
